import React from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowDown,
  ArrowRight,
  BookMarked,
  BookOpen,
  Camera,
  Check,
  CheckCircle2,
  Clock3,
  Code2,
  Globe2,
  GraduationCap,
  Heart,
  Info,
  MapPin,
  MessageCircle,
  MoveHorizontal,
  NotebookPen,
  Quote,
  Rows3,
  Scale,
  School,
  Search,
  ShieldCheck,
  Sparkles,
  WalletCards,
  XCircle,
  Users,
  Star as StarIcon,
} from "lucide-react";
import Button from "../components/Button";
import SchoolCard from "../components/SchoolCard";
import { Dots, Star } from "../components/Decor";
import DecorBlob from "../components/DecorBlob";
import AccentRays from "../components/decor/AccentRays";
import HandDrawnUnderline from "../components/decor/HandDrawnUnderline";
import brandLogo from "../assets/images/logo-fix.png";
import { schools } from "../data/schools";
import { formatRupiahShort } from "../lib/format";
import SEO from "../components/SEO";

const featured = schools.slice(0, 6);
const compareRows = [schools[0], schools[1], schools[2]];
const problemSchool = schools[0];
const comparisonSchool = schools[1];
const journeySchools = schools.slice(0, 3);
const landingTestimonials = [
  {
    id: "sample-parent-a",
    title: "Pilihan sekolah jadi lebih mudah dibandingkan.",
    quote:
      "Informasi biaya, kurikulum, dan fasilitas tersusun lebih rapi sehingga saya tidak perlu membuka banyak sumber satu per satu.",
    author: "Contoh Orang Tua A",
    role: "Contoh pengalaman pengguna",
    accent: "mint",
    avatar: "A",
    isSample: true,
  },
  {
    id: "sample-parent-b",
    title: "Lebih mudah melihat sekolah yang sesuai kebutuhan.",
    quote:
      "Dengan mencatat lokasi, budget, dan minat anak, pilihan sekolah terasa lebih terarah dan lebih mudah dipahami.",
    author: "Contoh Orang Tua B",
    role: "Contoh pengalaman pengguna",
    accent: "lavender",
    avatar: "B",
    isSample: true,
  },
  {
    id: "sample-parent-c",
    title: "Lebih tenang sebelum menentukan pilihan.",
    quote:
      "Semua informasi penting bisa dilihat dan dibandingkan dalam satu pengalaman, sehingga proses memilih sekolah terasa lebih sederhana.",
    author: "Contoh Orang Tua C",
    role: "Contoh pengalaman pengguna",
    accent: "yellow",
    avatar: "C",
    isSample: true,
  },
];
const testimonialsAreSample = landingTestimonials.some((testimonial) => testimonial.isSample);

export default function Landing() {
  return (
    <div className="overflow-hidden">
      <SEO
        canonicalPath="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "MySchoolMate",
          url: "https://myschoolmate.net/",
          logo: "https://myschoolmate.net/assets/logo-fix.png",
        }}
      />
      {/* ---------------- HERO ---------------- */}
      <section className="relative overflow-hidden border-b-2 border-ink bg-cream">
        <DecorBlob
          className="pointer-events-none absolute -right-24 -top-24 z-0 h-72 w-72 opacity-45 sm:h-96 sm:w-96"
          color="#73DCC6"
        />
        <DecorBlob
          className="pointer-events-none absolute -left-10 top-1/2 z-0 hidden h-64 w-64 opacity-40 xl:block"
          color="#FFD966"
        />

        <div className="relative z-10 mx-auto max-w-7xl px-4 pb-12 pt-10 sm:px-6 sm:pb-16 sm:pt-14 lg:px-8 xl:pb-20 xl:pt-16">
          <div className="grid items-center gap-10 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] xl:gap-8">
            <div className="animate-fade-up mx-auto w-full max-w-2xl text-center xl:mx-0 xl:max-w-none xl:text-left">
              <span className="inline-flex items-center gap-2 rounded-pill bg-yellow px-4 py-1.5 text-sm font-bold ink-border">
                <Sparkles aria-hidden="true" className="h-4 w-4" />
                Temukan sekolah lebih mudah
              </span>

              <h1 className="mt-5 text-balance text-4xl font-extrabold leading-tight tracking-normal sm:text-5xl lg:text-[3.5rem] xl:text-[3.2rem]">
                Cari sekolah paling{" "}
                <span className="relative inline-block">
                  tepat
                  <HandDrawnUnderline
                    variant="scribble"
                    color="coral"
                    className="absolute -bottom-3 left-[-6%] h-4 w-[112%]"
                  />
                </span>{" "}
                untuk masa depan anak Anda
              </h1>

              <p className="mx-auto mt-6 max-w-xl text-base font-medium leading-relaxed text-ink/75 sm:text-lg xl:mx-0 xl:max-w-lg">
                Lihat suasana sekolah, program unggulan, lokasi, dan biaya dalam satu tempat.
                MySchoolMate membantu keluarga memilih dengan lebih yakin.
              </p>

              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row xl:justify-start">
                <Button to="/match" size="lg" variant="yellow" data-testid="hero-cta-match">
                  <Sparkles aria-hidden="true" size={18} />
                  Cari dengan AI
                </Button>
                <Button to="/schools" variant="secondary" size="lg" data-testid="hero-cta-explore">
                  Jelajahi Sekolah
                  <ArrowRight aria-hidden="true" size={18} />
                </Button>
              </div>

              <p className="mt-4 text-sm font-semibold text-ink/55">
                Tidak perlu membuka puluhan website sekolah.
              </p>
            </div>

            <div
              className="animate-fade-up relative mx-auto h-[430px] w-full max-w-[740px] sm:h-[535px] md:h-[590px] xl:h-[600px]"
              style={{ animationDelay: "120ms" }}
            >
              <Star
                className="pointer-events-none absolute left-[8%] top-3 z-30 h-8 w-8 sm:left-[12%] sm:h-10 sm:w-10"
                color="#6554E8"
              />
              <Dots
                className="pointer-events-none absolute -bottom-10 -right-6 z-0 hidden h-20 w-20 opacity-55 sm:block"
                color="#211A4D"
              />

              <div className="absolute left-[2%] right-[2%] top-9 h-[235px] sm:left-[7%] sm:right-[5%] sm:top-12 sm:h-[350px] md:h-[410px] xl:left-[9%] xl:right-[1%] xl:top-14 xl:h-[450px]">
                <div className="absolute inset-0 -translate-x-1 sm:-translate-x-2 translate-y-1 sm:translate-y-2 rounded-[54px_20px_54px_20px] bg-ink sm:rounded-[78px_26px_78px_26px]" />
                <div className="relative h-full overflow-hidden rounded-[54px_20px_54px_20px] border-2 border-warm bg-cream-2 sm:rounded-[78px_26px_78px_26px]">
                  <img
                    src="/assets/hero/hero-students.webp"
                    alt="Siswa berdiskusi bersama di lingkungan sekolah"
                    width="1400"
                    height="747"
                    fetchpriority="high"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
              </div>

              <div className="absolute right-[3%] top-[238px] z-20 h-[118px] w-[48%] sm:top-[350px] sm:h-[152px] sm:w-[45%] md:top-[408px] md:h-[160px] xl:right-0 xl:top-[412px] xl:h-[168px] xl:w-[46%]">
                <div className="absolute inset-0 translate-x-1 sm:translate-x-2 translate-y-1 sm:translate-y-2 rounded-[22px] bg-ink sm:rounded-[28px]" />
                <div className="relative h-full overflow-hidden rounded-[22px] border-2 border-warm bg-cream-2 sm:rounded-[28px]">
                  <img
                    src="/assets/hero/hero-parent-school.webp"
                    alt="Orang tua berdiskusi dengan perwakilan sekolah"
                    width="800"
                    height="451"
                    loading="eager"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
              </div>

              <HeroInfoCard
                className="top-[5%] left-0 z-30 sm:top-[12%] sm:-left-2 md:-left-2 xl:-left-4"
                icon={ShieldCheck}
                iconClassName="bg-mint/50"
                title="Verified demo profile"
                text="Data sekolah diverifikasi"
              />
              <HeroInfoCard
                className="top-[22%] left-0 z-30 sm:top-[26%] sm:-left-2 md:-left-2 xl:-left-4"
                icon={Sparkles}
                iconClassName="bg-yellow"
                title="93% Match"
                text="Cocok untuk anak Anda"
                accent
              />
              <HeroInfoCard
                className="top-[39%] left-0 z-30 sm:top-[40%] sm:-left-2 md:-left-2 xl:-left-4"
                icon={MapPin}
                iconClassName="bg-mint/50"
                title="Bandung"
                text="4,2 km dari lokasi Anda"
              />
              <HeroInfoCard
                className="top-[56%] left-0 z-30 sm:top-[54%] sm:-left-2 md:-left-2 xl:-left-4"
                icon={BookOpen}
                iconClassName="bg-purple/15"
                title="Islam Terpadu"
                text="Program unggulan"
              />
              <HeroInfoCard
                className="-bottom-4 left-[30%] z-30 sm:-bottom-6 sm:left-[35%] md:left-[40%] xl:-bottom-8 xl:left-[45%]"
                icon={WalletCards}
                iconClassName="bg-mint/50"
                title="Rp1,8 jt/bulan"
                text="Estimasi biaya"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- PROBLEM ---------------- */}
      <section
        id="school-choice-story"
        aria-labelledby="school-choice-heading"
        className="relative overflow-hidden bg-[#B8EBF9]"
      >
        <DecorBlob
          className="pointer-events-none absolute -left-28 top-8 h-64 w-64 opacity-50"
          color="#DCD5FF"
        />
        <DecorBlob
          className="pointer-events-none absolute -right-12 top-40 h-72 w-72 opacity-30"
          color="#FFD966"
        />

        <div className="relative z-10 mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8 xl:py-16">
          <div className="grid items-start gap-12 xl:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] xl:gap-8">
            <div className="mx-auto w-full max-w-2xl xl:mx-0 xl:max-w-none xl:pt-4">
              <span className="inline-block rounded-full bg-purple/10 px-3 py-1 text-xs font-bold tracking-wider text-purple uppercase mb-2">
                TENTANG KAMI
              </span>

              <h2
                id="school-choice-heading"
                className="relative mt-8 text-4xl font-extrabold leading-tight tracking-normal sm:text-5xl xl:text-[3.25rem]"
              >
                <AccentRays
                  variant="curved-rays"
                  color="mint"
                  rotation={12}
                  className="absolute -right-1 -top-8 h-10 w-10 sm:-right-5 sm:-top-6 xl:-right-7 xl:top-0"
                />
                <span className="block">Memilih sekolah</span>
                <span className="block xl:whitespace-nowrap">seharusnya tidak</span>
                <span className="relative inline-block">
                  serumit ini.
                  <HandDrawnUnderline
                    variant="marker"
                    color="purple"
                    className="absolute -bottom-2 left-[-2%] h-3 w-[104%]"
                  />
                </span>
              </h2>

              <p className="mt-6 max-w-[465px] text-base font-medium leading-relaxed text-ink/70 sm:text-lg">
                Dulu keluarga harus mencari informasi sekolah satu per satu. Kini MySchoolMate
                membantu menyusun pilihan sekolah dalam satu pengalaman yang lebih sederhana.
              </p>

              <OldWayPanel />
            </div>

            <div className="relative mx-auto w-full max-w-[820px] md:h-[620px] xl:h-[650px]">
              <DecorBlob
                className="pointer-events-none absolute bottom-8 right-0 z-0 hidden h-64 w-64 opacity-60 xl:block"
                color="#73DCC6"
              />
              <Star
                className="pointer-events-none absolute right-[12%] top-2 z-20 hidden h-9 w-9 sm:block xl:right-[16%] xl:top-1"
                color="#6554E8"
              />
              <span
                className="pointer-events-none absolute left-0 top-4 z-20 flex h-14 w-14 items-center justify-center rounded-full border-2 border-ink bg-yellow sm:left-[3%] sm:h-16 sm:w-16 xl:left-[9%] xl:top-[7%]"
                aria-hidden="true"
              >
                <Heart className="h-6 w-6" />
              </span>

              <div className="relative z-10 mt-8 h-[330px] sm:h-[455px] md:absolute md:left-[4%] md:top-6 md:mt-0 md:h-[520px] md:w-[92%] xl:left-[10%] xl:h-[520px] xl:w-[85%]">
                <div className="absolute inset-0 -translate-x-2 translate-y-2 rounded-[58px_22px_72px_22px] bg-ink sm:-translate-x-3 sm:translate-y-3 sm:rounded-[86px_28px_96px_28px]" />
                <div className="relative h-full overflow-hidden rounded-[58px_22px_72px_22px] border-2 border-warm bg-cream-2 sm:rounded-[86px_28px_96px_28px]">
                  <img
                    src="/assets/landing/school-choice-parent-child.webp"
                    alt="Orang tua dan anak melihat informasi pilihan sekolah bersama"
                    width="1280"
                    height="854"
                    loading="lazy"
                    className="h-full w-full object-cover object-[44%_center]"
                  />
                </div>
              </div>

              <div className="hidden md:contents">
                <DiscoveryActionCard
                  className="md:absolute md:left-[58%] md:top-[30px] md:w-[300px] xl:left-[62%] xl:top-[50px] xl:w-[320px]"
                  icon={Search}
                  iconClassName="bg-mint"
                  title="Cari"
                  text="Sekolah terbaik terdekat"
                />
                <DiscoveryActionCard
                  className="md:absolute md:left-[58%] md:top-[120px] md:w-[300px] xl:left-[62%] xl:top-[140px] xl:w-[320px]"
                  icon={Scale}
                  iconClassName="bg-purple text-warm"
                  title="Bandingkan"
                  text="Bandingkan beberapa sekolah sekaligus"
                />
              </div>

              <SchoolMatchPreview
                school={{
                  name: "Pelita Bangsa",
                  district: "Jakarta Selatan",
                  matchScore: 93,
                  tags: ["K. Merdeka", "Bilingual", "Ekskul"]
                }}
                className="hidden md:block md:absolute md:left-[58%] md:top-[220px] md:w-[300px] xl:left-[62%] xl:top-[250px] xl:mt-0 xl:w-[320px]"
              />

              {/* <img
                src="/assets/landing/dulu-kini.png"
                alt="Dulu dan Kini"
                className="mt-6 relative z-50 w-full sm:w-[80%] mx-auto object-contain xl:absolute xl:bottom-[40px] xl:-left-[5%] xl:mt-0 xl:w-[320px]"
              /> */}
              <ComparisonPreview
                schools={[problemSchool, comparisonSchool]}
                className="hidden md:block md:absolute md:left-[58%] md:top-[360px] md:w-[300px] xl:bottom-[25px] xl:top-auto xl:left-[62%] xl:mt-0 xl:w-[320px]"
              />

              <Dots
                className="pointer-events-none absolute bottom-8 right-0 z-20 hidden h-20 w-20 opacity-45 xl:block"
                color="#211A4D"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- MATCH JOURNEY ---------------- */}
      <section
        aria-labelledby="match-journey-heading"
        className="relative overflow-hidden border-t-2 border-ink bg-[#ECEBFE]"
      >
        <DecorBlob
          className="pointer-events-none absolute -bottom-24 -left-20 h-56 w-56 opacity-50"
          color="#73DCC6"
        />
        <Star
          className="pointer-events-none absolute right-[8%] top-24 hidden h-7 w-7 sm:block"
          color="#FFD966"
        />
        <Dots
          className="pointer-events-none absolute bottom-24 right-[2%] hidden h-20 w-20 opacity-50 lg:block"
          color="#F58B72"
        />

        <div className="relative z-10 mx-auto max-w-[1180px] px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
          <header className="mx-auto max-w-[880px] text-center">
            <h2
              id="match-journey-heading"
              className="text-3xl font-extrabold leading-tight sm:text-4xl lg:text-[3rem]"
            >
              <span className="block">Setiap anak berbeda.</span>
              <span className="block">
                Sekolah yang{" "}
                <span className="relative inline-block">
                  cocok
                  <HandDrawnUnderline
                    variant="double-marker"
                    color="yellow"
                    className="absolute -bottom-2 left-[-3%] h-3 w-[106%]"
                  />
                </span>{" "}
                juga berbeda.
              </span>
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-base font-medium leading-relaxed text-ink/70 sm:text-lg">
              Ceritakan kebutuhan keluarga Anda, lalu MySchoolMate membantu mencocokkan dengan
              pilihan sekolah yang paling sesuai.
            </p>
          </header>

          <div className="mx-auto mt-12 grid max-w-md gap-16 lg:max-w-none lg:grid-cols-3 lg:gap-12">
            <div className="relative">
              <MatchJourneyCard
                number="01"
                accent="mint"
                title="Ceritakan kebutuhan Anda"
                description="Pilih lokasi, jenjang, kurikulum, dan anggaran yang paling penting bagi keluarga."
              >
                <div className="space-y-2.5">
                  <JourneyPreference icon={MapPin} label="Bandung" />
                  <JourneyPreference icon={GraduationCap} label="SD" />
                  <JourneyPreference icon={BookOpen} label="Islam Terpadu" />
                  <JourneyPreference icon={WalletCards} label={"< Rp2 jt"} />
                </div>
                <img
                  src="/assets/landing/match-step-needs.webp"
                  alt="Orang tua mencari pilihan sekolah menggunakan laptop"
                  width="768"
                  height="512"
                  loading="lazy"
                  className="mx-auto mt-auto -mb-5 h-36 w-full object-contain object-bottom pt-3 sm:-mb-6 sm:h-40"
                />
              </MatchJourneyCard>
              <JourneyConnector accent="mint" />
            </div>

            <div className="relative">
              <MatchJourneyCard
                number="02"
                accent="yellow"
                title="Kami mencocokkan"
                description="MySchoolMate mencocokkan kebutuhan keluarga dengan informasi sekolah yang tersedia."
              >
                <div aria-label="Contoh dimensi pencocokan">
                  <StaticRadarChart />
                </div>
                <img
                  src="/assets/landing/match-step-process.webp"
                  alt="Asisten MySchoolMate membantu mencocokkan pilihan sekolah"
                  width="768"
                  height="820"
                  loading="lazy"
                  className="mx-auto mt-auto -mb-5 h-36 w-full object-contain object-bottom pt-3 sm:-mb-6 sm:h-40"
                />
              </MatchJourneyCard>
              <JourneyConnector accent="yellow" />
            </div>

            <div className="relative">
              <MatchJourneyCard
                number="03"
                accent="coral"
                title="Dapatkan pilihan terbaik"
                description="Lihat rekomendasi sekolah yang paling sesuai dengan kebutuhan keluarga Anda."
              >
                <div className="space-y-2.5">
                  {journeySchools.map((school, index) => (
                    <JourneyRecommendation
                      key={school.id}
                      school={school}
                      score={[93, 88, 81][index]}
                      accent={["mint", "yellow", "coral"][index]}
                    />
                  ))}
                </div>
                <img
                  src="/assets/landing/match-step-result.webp"
                  alt="Ilustrasi sekolah sebagai hasil rekomendasi"
                  width="768"
                  height="512"
                  loading="lazy"
                  className="mx-auto mt-auto -mb-5 h-36 w-full object-contain object-bottom pt-3 sm:-mb-6 sm:h-40"
                />
              </MatchJourneyCard>
            </div>
          </div>

          <div className="mt-12 flex justify-center lg:mt-10">
            <Button to="/match" size="lg" data-testid="how-cta-match">
              Coba AI School Match
              <ArrowRight aria-hidden="true" size={18} />
            </Button>
          </div>
        </div>
      </section>

      {/* ---------------- FEATURED SCHOOLS ---------------- */}
      <section className="bg-cream border-t-2 border-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <h2 className="text-3xl sm:text-4xl font-extrabold max-w-xl">
              Sekolah yang mungkin cocok untuk{" "}
              <span className="relative inline-block">
                keluarga Anda
                <HandDrawnUnderline
                  variant="swoosh"
                  color="coral"
                  className="absolute -bottom-2 left-[-3%] h-3 w-[106%]"
                />
              </span>
            </h2>
            <Link
              to="/schools"
              className="font-bold text-purple hover:underline shrink-0"
              data-testid="featured-see-all"
            >
              Lihat semua →
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((s) => (
              <SchoolCard key={s.id} school={s} />
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- COMPARE PREVIEW ---------------- */}
      <section
        aria-labelledby="landing-compare-heading"
        className="relative overflow-hidden border-b-2 border-ink bg-mint"
        style={{
          backgroundImage:
            "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.48), transparent 44%), linear-gradient(135deg, #8AE4D1 0%, #73DCC6 48%, #62D0BA 100%)",
        }}
      >
        <DecorBlob
          className="pointer-events-none absolute -left-24 top-20 h-72 w-72 opacity-20"
          color="#FFFDFC"
        />
        <DecorBlob
          className="pointer-events-none absolute -bottom-32 -right-20 h-96 w-96 opacity-20"
          color="#FFFDFC"
        />
        <Dots
          className="pointer-events-none absolute right-[4%] top-16 hidden h-24 w-24 opacity-20 sm:block"
          color="#211A4D"
        />
        <Star
          className="pointer-events-none absolute bottom-24 left-[6%] hidden h-7 w-7 opacity-70 lg:block"
          color="#FFFDFC"
        />

        <div className="relative z-10 mx-auto max-w-[1320px] px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
          <header className="mx-auto max-w-[760px] text-center">
            <h2
              id="landing-compare-heading"
              className="text-3xl font-extrabold leading-tight sm:text-4xl lg:text-[3rem]"
            >
              <span className="block">Masih bingung memilih?</span>
              <span className="block">
                <span className="relative inline-block">
                  Bandingkan
                  <HandDrawnUnderline
                    variant="brush"
                    color="yellow"
                    className="absolute -bottom-2 left-[-3%] h-3 w-[106%]"
                  />
                </span>{" "}
                saja.
              </span>
            </h2>
            <p className="mx-auto mt-6 max-w-[680px] text-base font-medium leading-relaxed text-ink/75 sm:text-lg">
              Lihat perbedaan biaya, lokasi, kurikulum, dan kecocokan beberapa sekolah dalam satu
              tampilan.
            </p>
          </header>

          <div className="relative mx-auto mt-12 max-w-[1240px]">
            <div className="mx-auto mb-6 grid max-w-[680px] grid-cols-2 gap-3 xl:hidden">
              <ComparePriority icon={WalletCards} label="Biaya" accent="yellow" />
              <ComparePriority icon={MapPin} label="Lokasi" accent="purple" />
              <ComparePriority icon={BookOpen} label="Kurikulum" accent="mint" />
              <ComparePriority icon={Heart} label="Minat Anak" accent="coral" />
            </div>

            <div className="hidden xl:block">
              <ComparePriority
                icon={WalletCards}
                label="Biaya"
                accent="yellow"
                className="absolute -left-16 top-[20%] -rotate-2"
              />
              <ComparePriority
                icon={MapPin}
                label="Lokasi"
                accent="purple"
                className="absolute bottom-[18%] -left-16 rotate-1"
              />
              <ComparePriority
                icon={BookOpen}
                label="Kurikulum"
                accent="mint"
                className="absolute -right-16 top-[20%] rotate-2"
              />
              <ComparePriority
                icon={Heart}
                label="Minat Anak"
                accent="coral"
                className="absolute bottom-[18%] -right-16 -rotate-1"
              />
              <CompareConnectorLines />
            </div>

            <LandingComparisonBoard schools={compareRows} />
          </div>

          <div className="mt-10 flex justify-center">
            <Button to="/compare" size="lg" data-testid="compare-preview-cta">
              <Scale aria-hidden="true" size={18} />
              Bandingkan Sekolah
              <ArrowRight aria-hidden="true" size={18} />
            </Button>
          </div>
        </div>
      </section>


      {/* ---------------- TESTIMONIALS ---------------- */}
      <section
        aria-labelledby="landing-testimonials-heading"
        className="relative overflow-hidden bg-cream"
      >
        <DecorBlob
          className="pointer-events-none absolute -right-28 -top-24 h-80 w-80 opacity-30"
          color="#73DCC6"
        />
        <Star
          className="pointer-events-none absolute left-[7%] top-24 hidden h-8 w-8 opacity-80 sm:block"
          color="#6554E8"
        />
        <Star
          className="pointer-events-none absolute bottom-20 right-[7%] hidden h-7 w-7 opacity-75 sm:block"
          color="#6554E8"
        />
        <Dots
          className="pointer-events-none absolute right-[4%] top-[38%] hidden h-20 w-20 opacity-20 lg:block"
          color="#211A4D"
        />

        <div className="relative z-10 mx-auto max-w-[1240px] px-4 py-20 sm:px-6 sm:py-24 lg:px-8 lg:py-28">
          <header className="mx-auto max-w-[780px] text-center">
            <span className="inline-flex items-center gap-2 rounded-pill border-2 border-ink bg-yellow px-4 py-2 text-xs font-extrabold uppercase text-ink sm:text-sm">
              <MessageCircle aria-hidden="true" className="h-4 w-4" />
              Cerita Keluarga
            </span>

            <h2
              id="landing-testimonials-heading"
              className="mt-7 text-4xl font-extrabold leading-tight tracking-normal text-ink sm:text-5xl lg:text-[3.45rem]"
            >
              <span className="block">Cerita dari keluarga</span>
              <span className="block">
                yang{" "}
                <span className="relative inline-block">
                  terbantu
                  <HandDrawnUnderline
                    variant="swoosh"
                    color="mint"
                    className="absolute -bottom-2 left-[-3%] h-3 w-[106%]"
                  />
                </span>
              </span>
            </h2>

            <p className="mx-auto mt-5 max-w-[690px] text-base font-medium leading-relaxed text-ink/70 sm:text-lg">
              Ribuan orang tua menemukan sekolah yang tepat dan melihat anak mereka tumbuh percaya diri.
            </p>

            <div className="mx-auto mt-7 flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 rounded-pill border border-ink/20 px-5 py-2.5">
                <StarIcon aria-hidden="true" className="h-5 w-5 text-yellow fill-yellow" />
                <span className="font-extrabold text-ink text-lg">4.8/5</span>
                <span className="text-ink/70 font-medium text-sm">dari ulasan keluarga</span>
              </div>
              <div className="flex items-center gap-2 rounded-pill border border-ink/20 px-5 py-2.5">
                <Users aria-hidden="true" className="h-5 w-5 text-purple fill-purple" />
                <span className="font-extrabold text-ink text-lg">120+</span>
                <span className="text-ink/70 font-medium text-sm">cerita positif</span>
              </div>
            </div>
          </header>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:mt-14 xl:grid-cols-3">
            {landingTestimonials.map((testimonial, index) => (
              <LandingTestimonialCard
                key={testimonial.id}
                testimonial={testimonial}
                className={
                  index === 2
                    ? "md:col-span-2 md:mx-auto md:w-[calc(50%-0.75rem)] xl:col-span-1 xl:w-auto"
                    : ""
                }
              />
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- BENEFITS ---------------- */}
      <section
        aria-labelledby="landing-benefits-heading"
        className="relative overflow-hidden bg-cream"
      >
        <DecorBlob
          className="pointer-events-none absolute -left-28 top-10 h-80 w-80 opacity-20"
          color="#DCD5FF"
        />
        <Dots
          className="pointer-events-none absolute right-[3%] top-[36%] hidden h-24 w-24 opacity-20 lg:block"
          color="#6554E8"
        />
        <BenefitsEdgeLines />

        <div className="relative z-10 mx-auto max-w-[1280px] px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
          <header className="mx-auto max-w-[760px] text-center">
            <span className="inline-flex items-center gap-2 rounded-pill border border-ink/20 bg-mint/20 px-4 py-2 text-xs font-extrabold uppercase text-ink sm:text-sm">
              <Sparkles aria-hidden="true" className="h-4 w-4 text-purple" />
              Keuntungan MySchoolMate
            </span>
            <h2
              id="landing-benefits-heading"
              className="mt-10 text-3xl font-extrabold leading-tight sm:text-4xl lg:text-[3.35rem]"
            >
              <span className="block">Kenapa keluarga</span>
              <span className="block">
                memilih{" "}
                <span className="relative inline-block">
                  MySchoolMate
                  <HandDrawnUnderline
                    variant="wavy"
                    color="mint"
                    className="absolute -bottom-2 left-[-3%] h-3 w-[106%]"
                  />
                </span>{"?"}
              </span>
            </h2>
            <p className="mx-auto mt-5 max-w-[700px] text-base font-medium leading-relaxed text-ink/70 sm:text-lg">
              MySchoolMate membantu keluarga menemukan, memahami, dan membandingkan pilihan
              sekolah dengan lebih mudah.
            </p>
          </header>

          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
            <LandingBenefitCard
              icon={Sparkles}
              accent="mint"
              title="AI School Match"
              description="Ceritakan kebutuhan keluarga dan anak dengan bahasa Anda sendiri. MySchoolMate membantu mencocokkannya dengan pilihan sekolah yang tersedia."
              summary="Rekomendasi berdasarkan kebutuhan keluarga"
            />
            <LandingBenefitCard
              icon={Scale}
              accent="yellow"
              title="Bandingkan dengan mudah"
              description="Lihat biaya, kurikulum, fasilitas, dan informasi penting beberapa sekolah dalam satu tampilan."
              summary="Lebih mudah melihat perbedaannya"
            />
            <LandingBenefitCard
              icon={ShieldCheck}
              accent="mint"
              title="Informasi lebih jelas"
              description="Informasi sekolah disusun dalam kategori yang rapi agar lebih mudah dibaca dan dibandingkan."
              summary="Informasi penting lebih mudah dipahami"
            />
            <LandingBenefitCard
              icon={School}
              accent="yellow"
              title="Kenali sekolah sebelum berkunjung"
              description="Lihat profil, foto, fasilitas, program, biaya, dan informasi penting lainnya sebelum menentukan pilihan."
              summary="Lebih siap sebelum menentukan pilihan"
            />
          </div>
        </div>
      </section>

      {/* ---------------- FINAL CTA ---------------- */}
      <section
        aria-labelledby="final-landing-cta-heading"
        className="mx-auto max-w-[1280px] px-4 pb-4 sm:px-6 lg:px-8"
      >
        <div className="relative overflow-hidden rounded-[30px] bg-ink text-white xl:min-h-[340px]">
          <DecorBlob
            className="pointer-events-none absolute -left-20 -top-24 z-0 h-56 w-56 opacity-20"
            color="#DCD5FF"
          />
          <Dots
            className="pointer-events-none absolute left-[47%] top-8 z-10 hidden h-16 w-16 opacity-30 xl:block"
            color="#73DCC6"
          />
          <Star
            className="pointer-events-none absolute right-8 top-8 z-30 h-8 w-8"
            color="#FFD966"
          />
          <Star
            className="pointer-events-none absolute bottom-[27%] left-[52%] z-30 hidden h-6 w-6 xl:block"
            color="#73DCC6"
          />

          <div className="grid xl:min-h-[340px] xl:grid-cols-[minmax(0,1.28fr)_minmax(0,1fr)]">
            <div className="relative z-20 flex flex-col justify-center px-6 py-12 sm:px-10 sm:py-14 lg:px-12 xl:px-12 xl:py-10">
              <span className="text-xs font-extrabold uppercase text-mint sm:text-sm">
                Siap memulai?
              </span>
              <h2
                id="final-landing-cta-heading"
                className="mt-3 max-w-[650px] text-3xl font-extrabold leading-tight text-white sm:text-4xl lg:text-[2.75rem] xl:text-[2.5rem]"
              >
                <span className="block xl:whitespace-nowrap">Siap menemukan sekolah</span>
                <span className="block xl:whitespace-nowrap">yang cocok untuk anak Anda?</span>
              </h2>
              <p className="mt-5 max-w-[590px] text-base font-medium leading-relaxed text-white/80 sm:text-lg lg:max-w-[550px]">
                Mulai dari kebutuhan keluarga Anda, lalu temukan pilihan sekolah yang paling sesuai.
              </p>

              <div className="mt-7 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-4">
                <Button
                  to="/match"
                  variant="yellow"
                  size="md"
                  className="sm:w-auto"
                  data-testid="final-cta-match"
                >
                  <Sparkles aria-hidden="true" size={18} />
                  Mulai AI School Match
                </Button>
                <Button
                  to="/schools"
                  size="md"
                  className="!border-white !bg-transparent !text-white hover:!bg-white hover:!text-ink sm:w-auto"
                  data-testid="final-cta-explore"
                >
                  Jelajahi Sekolah
                  <ArrowRight aria-hidden="true" size={18} />
                </Button>
              </div>
            </div>

            <div className="relative min-h-[250px] overflow-hidden [clip-path:polygon(0_12%,18%_0,100%_0,100%_100%,0_100%)] sm:min-h-[300px] xl:min-h-[340px] xl:[clip-path:polygon(14%_0,100%_0,100%_100%,0_100%,8%_78%,3%_50%,8%_22%)]">
              <img
                src="/assets/landing/3people.png"
                alt="Keluarga mencari informasi sekolah bersama"
                width="1280"
                height="854"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover object-[43%_center]"
              />
              <div
                className="pointer-events-none absolute inset-y-0 left-0 w-[38%] bg-gradient-to-r from-ink/75 via-ink/25 to-transparent"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function OldWayPanel() {
  const steps = [
    { label: "Google", icon: Search },
    { label: "Socmed", icon: Camera },
    { label: "WA", icon: MessageCircle },
    { label: "Web", icon: Globe2 },
    { label: "Kunjungan", icon: MapPin },
    { label: "Catat Manual", icon: NotebookPen },
  ];

  return (
    <div className="mt-8 rounded-[22px] border-2 border-ink bg-warm p-4 sm:p-5 xl:w-[94%] xl:p-4">
      <p className="text-xs font-extrabold uppercase text-coral">
        Dulu: mencari informasi satu per satu
      </p>

      <div className="mt-3.5 flex flex-wrap items-center gap-1.5">
        {steps.map(({ label, icon: Icon }, index) => (
          <span key={label} className="inline-flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1.5 rounded-pill border border-ink bg-warm px-2.5 py-1 text-[11px] font-bold">
              <Icon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
              {label}
            </span>
            {index < steps.length - 1 && (
              <ArrowRight aria-hidden="true" className="h-4 w-4 shrink-0 text-ink/35" />
            )}
          </span>
        ))}
      </div>

      <p className="mt-3 flex items-start gap-2 text-xs font-medium leading-relaxed text-ink/60">
        <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-coral" />
        Banyak waktu, informasi tersebar, dan sulit dibandingkan.
      </p>
    </div>
  );
}

function DiscoveryActionCard({ className = "", icon: Icon, iconClassName, title, text }) {
  return (
    <div
      className={`relative z-30 flex w-full items-center gap-3 rounded-[16px] border border-ink bg-warm px-3 py-3 shadow-soft sm:gap-4 sm:px-4 sm:py-4 ${className}`}
    >
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconClassName}`}
      >
        <Icon aria-hidden="true" className="h-5 w-5" />
      </span>
      <span className="min-w-0">
        <strong className="block text-sm font-extrabold leading-tight sm:text-base">{title}</strong>
        <span className="mt-0.5 block text-[10px] font-medium leading-tight text-ink/60 sm:text-xs">
          {text}
        </span>
      </span>
    </div>
  );
}

function SchoolMatchPreview({ school, className = "" }) {
  return (
    <div className={`relative z-40 w-full rounded-[18px] border border-ink bg-warm p-4 shadow-soft sm:p-5 xl:p-4 ${className}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 shrink-0 bg-purple/10 border border-purple/20 text-purple rounded-lg flex items-center justify-center font-bold">
            <School size={20} />
          </div>
          <div className="min-w-0">
            <p className="truncate text-base font-extrabold sm:text-lg">{school.name}</p>
            <p className="mt-0.5 flex items-center gap-1 text-xs font-semibold text-ink/60 sm:text-sm">
              <MapPin aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-purple" />
              {school.district}
            </p>
          </div>
        </div>
        <div className="shrink-0 rounded-[12px] bg-mint/45 px-2 py-1.5 text-center">
          <span className="block text-[9px] font-bold uppercase text-ink/60">Cocok</span>
          <strong className="text-lg font-extrabold">{school.matchScore}%</strong>
        </div>
      </div>

      <div className="mt-3 flex gap-1.5 border-t border-ink/10 pt-3 overflow-hidden">
        {school.tags.slice(0, 3).map((tag, index) => (
          <span
            key={tag}
            className={`whitespace-nowrap rounded-pill px-2.5 py-1 text-[10px] font-bold sm:text-xs ${
              index === 0 ? "bg-mint/30" : index === 1 ? "bg-purple/10" : "bg-yellow/50"
            }`}
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

function BeforeAfterPanel({ className = "" }) {
  const before = ["Info tersebar", "Sulit dibandingkan", "Memakan waktu"];
  const after = ["Informasi lebih terpusat", "Mudah dibandingkan", "Hemat waktu"];

  return (
    <div className={`relative z-50 rounded-[20px] border-2 border-ink bg-warm p-4 shadow-soft ${className}`}>
      <div className="grid grid-cols-2 divide-x divide-ink/15">
        <div className="pr-3 sm:pr-4">
          <p className="text-center text-xs font-extrabold uppercase text-coral sm:text-sm">Dulu</p>
          <p className="mt-1 text-center text-[10px] font-semibold text-ink/60 sm:text-xs">
            Terpisah &amp; melelahkan
          </p>
          <div className="my-3 flex h-14 items-center justify-center gap-1.5 xl:my-2.5 xl:h-12" aria-hidden="true">
            <span className="rounded-full bg-coral/15 p-1.5"><Search className="h-4 w-4" /></span>
            <span className="border-t-2 border-dashed border-coral/60 w-4" />
            <span className="rounded-full bg-coral/15 p-1.5"><MessageCircle className="h-4 w-4" /></span>
            <span className="border-t-2 border-dashed border-coral/60 w-4" />
            <span className="rounded-full bg-coral/15 p-1.5"><Clock3 className="h-4 w-4" /></span>
          </div>
          <BeforeAfterList items={before} icon={XCircle} iconClassName="text-coral" />
        </div>

        <div className="pl-3 sm:pl-4">
          <p className="text-center text-xs font-extrabold uppercase text-mint sm:text-sm">Kini</p>
          <p className="mt-1 text-center text-[10px] font-semibold text-ink/60 sm:text-xs">
            Lebih terarah &amp; sederhana
          </p>
          <div className="my-3 flex h-14 items-center justify-center xl:my-2.5 xl:h-12" aria-hidden="true">
            <span className="relative flex h-12 w-12 items-center justify-center rounded-[16px] border border-ink bg-mint/30">
              <School className="h-5 w-5" />
              <CheckCircle2 className="absolute -right-1 -top-1 h-5 w-5 fill-mint text-ink" />
            </span>
          </div>
          <BeforeAfterList items={after} icon={CheckCircle2} iconClassName="text-mint" />
        </div>
      </div>
    </div>
  );
}

function BeforeAfterList({ items, icon: Icon, iconClassName }) {
  return (
    <ul className="space-y-2 xl:space-y-1.5">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-1.5 text-[10px] font-semibold leading-tight sm:text-xs">
          <Icon aria-hidden="true" className={`h-3.5 w-3.5 shrink-0 ${iconClassName}`} />
          {item}
        </li>
      ))}
    </ul>
  );
}

function ComparisonPreview({ schools: previewSchools, className = "" }) {
  return (
    <div className={`relative z-50 rounded-[16px] border border-ink bg-warm p-4 shadow-soft ${className}`}>
      <p className="text-sm font-extrabold mb-3">Perbandingan Sekolah</p>
      <div className="space-y-4">
        {previewSchools.map((school, index) => (
          <div key={school.id}>
            <div className="mb-2 flex items-center gap-3">
              <div className={`w-6 h-6 rounded flex items-center justify-center shrink-0 ${index === 0 ? "bg-yellow" : "bg-purple/20"}`}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={index === 0 ? "text-ink" : "text-purple"}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              </div>
              <div className="h-2.5 overflow-hidden rounded-pill bg-cream-2 flex-1">
                <div
                  className={`h-full rounded-pill ${index === 0 ? "bg-mint" : "bg-purple"}`}
                  style={{ width: `${school.matchScore}%` }}
                />
              </div>
              <span className="text-xs font-bold shrink-0">{school.matchScore}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HeroInfoCard({
  className = "",
  icon: Icon,
  iconClassName = "bg-cream-2",
  title,
  text,
  accent = false,
}) {
  return (
    <div
      className={`absolute flex max-w-[205px] items-center gap-2.5 rounded-[18px] border border-ink px-3 py-2.5 shadow-[0_8px_20px_-10px_rgba(33,26,77,0.5)] sm:max-w-[240px] sm:px-4 sm:py-3 ${
        accent ? "bg-yellow" : "bg-warm"
      } ${className}`}
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-ink/15 ${iconClassName}`}
      >
        <Icon aria-hidden="true" className="h-4 w-4 sm:h-5 sm:w-5" />
      </span>
      <span className="min-w-0">
        <strong className="block text-xs font-extrabold leading-tight sm:text-sm">{title}</strong>
        <span className="mt-0.5 hidden text-[10px] font-medium leading-tight text-ink/65 sm:block sm:text-[11px]">
          {text}
        </span>
      </span>
    </div>
  );
}

function MatchJourneyCard({ number, accent, title, description, children }) {
  const accentClasses = {
    mint: "bg-mint",
    yellow: "bg-yellow",
    coral: "bg-coral",
  };

  return (
    <article className="flex h-full flex-col overflow-hidden rounded-[28px] border-2 border-ink bg-white p-5 shadow-soft transition-transform duration-200 hover:-translate-y-1 sm:p-6 lg:h-[530px]">
      <div className="text-center">
        <span
          className={`inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-ink text-base font-extrabold ${accentClasses[accent]}`}
        >
          {number}
        </span>
        <h3 className="mx-auto mt-3 max-w-[260px] text-xl font-extrabold leading-tight">
          {title}
        </h3>
        <p className="mx-auto mt-2 max-w-[285px] text-xs font-medium leading-relaxed text-ink/65 sm:text-sm">
          {description}
        </p>
      </div>

      <div className="mt-5 flex min-h-0 flex-1 flex-col">{children}</div>
    </article>
  );
}

function JourneyPreference({ icon: Icon, label }) {
  return (
    <div className="flex min-h-10 items-center gap-3 rounded-pill border border-ink bg-warm px-4 py-2 text-sm font-bold">
      <Icon aria-hidden="true" className="h-4 w-4 shrink-0 text-purple" />
      <span>{label}</span>
    </div>
  );
}

function StaticRadarChart() {
  const data = [
    { label: "Lokasi", value: 91, icon: MapPin },
    { label: "Budget", value: 98, icon: WalletCards },
    { label: "Kurikulum", value: 96, icon: BookOpen },
    { label: "Minat", value: 88, icon: Heart },
    { label: "Fasilitas", value: 84, icon: School },
  ];

  const size = 160;
  const center = size / 2;
  const radius = 55; // outer pentagon radius

  const points = data.map((d, i) => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
    return {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle),
      valueX: center + radius * (d.value / 100) * Math.cos(angle),
      valueY: center + radius * (d.value / 100) * Math.sin(angle),
      labelX: center + (radius + 28) * Math.cos(angle),
      labelY: center + (radius + 24) * Math.sin(angle), // slightly closer on y to avoid cut off
    };
  });

  const outerPolygon = points.map(p => `${p.x},${p.y}`).join(" ");
  const innerPolygon = points.map(p => `${p.valueX},${p.valueY}`).join(" ");

  return (
    <div className="flex justify-center w-full my-2">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="overflow-visible absolute top-0 left-0">
          <polygon points={outerPolygon} fill="transparent" stroke="#211A4D" strokeWidth="1" strokeDasharray="3 3" opacity="0.3" />

          {points.map((p, i) => (
            <line key={`spoke-${i}`} x1={center} y1={center} x2={p.x} y2={p.y} stroke="#211A4D" strokeWidth="1" strokeDasharray="3 3" opacity="0.3" />
          ))}

          <polygon points={innerPolygon} fill="#73DCC6" opacity="0.8" stroke="#73DCC6" strokeWidth="1" />

          {points.map((p, i) => (
            <circle key={`point-${i}`} cx={p.valueX} cy={p.valueY} r="3" fill="#211A4D" />
          ))}
        </svg>

        {data.map((d, i) => {
          const p = points[i];
          const Icon = d.icon;
          return (
            <div key={`label-${i}`} className="absolute flex flex-col items-center justify-center text-ink w-16" style={{ left: p.labelX, top: p.labelY, transform: 'translate(-50%, -50%)' }}>
              <Icon className="mb-0.5 h-4 w-4 text-purple" />
              <span className="text-[10px] font-bold leading-tight">{d.label}</span>
              <span className="text-xs font-extrabold">{d.value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function JourneyRecommendation({ school, score, accent }) {
  const accentClasses = {
    mint: "bg-mint/55",
    yellow: "bg-yellow/70",
    coral: "bg-coral/65",
  };

  return (
    <div className="flex min-h-[58px] items-stretch overflow-hidden rounded-[16px] border border-ink bg-warm">
      <span
        className={`flex w-14 shrink-0 items-center justify-center border-r border-ink text-sm font-extrabold ${accentClasses[accent]}`}
      >
        {score}%
      </span>
      <span className="flex min-w-0 flex-1 items-center px-3 py-2 text-xs font-extrabold leading-snug sm:text-sm">
        {school.name}
      </span>
      <ArrowRight aria-hidden="true" className="mr-3 h-4 w-4 shrink-0 self-center text-ink/55" />
    </div>
  );
}

function JourneyConnector({ accent }) {
  const accentClasses = {
    mint: "bg-mint",
    yellow: "bg-yellow",
  };

  return (
    <span
      className={`pointer-events-none absolute -bottom-12 left-1/2 z-20 flex h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full border-2 border-ink shadow-soft lg:left-[calc(100%+24px)] lg:bottom-auto lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 ${accentClasses[accent]}`}
      aria-hidden="true"
    >
      <ArrowDown className="h-5 w-5 lg:hidden" />
      <ArrowRight className="hidden h-5 w-5 lg:block" />
    </span>
  );
}

function LandingTestimonialCard({ testimonial, className = "" }) {
  const accents = {
    mint: {
      surface: "bg-mint/25",
      avatar: "bg-mint/40",
      line: "bg-mint",
    },
    lavender: {
      surface: "bg-[#ECEBFE]",
      avatar: "bg-purple/10",
      line: "bg-purple",
    },
    yellow: {
      surface: "bg-yellow/25",
      avatar: "bg-yellow/40",
      line: "bg-yellow",
    },
  };
  const colors = accents[testimonial.accent] || accents.mint;

  return (
    <article
      className={`relative flex flex-col rounded-[28px] border-2 border-ink bg-white p-6 shadow-[0_18px_38px_-30px_rgba(33,26,77,0.45)] sm:p-8 xl:min-h-[430px] ${className}`}
    >
      <div className="flex items-start justify-between gap-4">
        <span
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-[16px] ${colors.surface}`}
          aria-hidden="true"
        >
          <Quote className="h-6 w-6 text-ink" strokeWidth={2.2} />
        </span>
        <span className={`mt-2 h-1 w-12 rounded-pill ${colors.line}`} aria-hidden="true" />
      </div>

      <h3 className="mt-6 text-xl font-extrabold leading-snug text-ink sm:text-[1.4rem]">
        “{testimonial.title}”
      </h3>
      <p className="mt-5 text-[15px] font-medium leading-[1.75] text-ink/70 sm:text-base">
        {testimonial.quote}
      </p>

      <div className="mt-auto pt-7">
        <div className="h-px bg-ink/10" aria-hidden="true" />
        <div className="mt-5 flex items-center gap-3">
          <span
            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-ink/10 text-base font-extrabold ${colors.avatar}`}
            aria-hidden="true"
          >
            {testimonial.avatar}
          </span>
          <span className="min-w-0 flex-1">
            <strong className="block text-sm font-extrabold text-ink sm:text-base">
              {testimonial.author}
            </strong>
            <span className="mt-0.5 block text-xs font-semibold text-ink/50 sm:text-sm">
              {testimonial.role}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}

function LandingBenefitCard({ icon: Icon, accent, title, description, summary }) {
  const accents = {
    mint: {
      icon: "bg-mint/25",
      line: "bg-mint",
      strip: "bg-mint/15",
      check: "text-[#2FAE93]",
    },
    yellow: {
      icon: "bg-yellow/35",
      line: "bg-yellow",
      strip: "bg-yellow/20",
      check: "text-[#C99500]",
    },
  };
  const colors = accents[accent];

  return (
    <article className="flex min-h-[452px] flex-col items-center rounded-[26px] border-2 border-ink bg-white p-6 text-center shadow-[0_14px_35px_-24px_rgba(33,26,77,0.4)] transition-transform duration-200 hover:-translate-y-1 sm:min-h-[456px] sm:p-7 xl:min-h-[460px]">
      <span
        className={`flex h-[86px] w-[86px] shrink-0 items-center justify-center rounded-full ${colors.icon}`}
        aria-hidden="true"
      >
        <Icon className="h-10 w-10 text-ink" strokeWidth={1.8} />
      </span>

      <h3 className="mt-6 flex min-h-[56px] items-center justify-center text-xl font-extrabold leading-tight xl:text-[1.35rem]">
        {title}
      </h3>
      <span className={`mt-3 h-0.5 w-10 rounded-pill ${colors.line}`} aria-hidden="true" />

      <p className="mt-5 text-[15px] font-medium leading-[1.65] text-ink/68 sm:text-base xl:text-[15px]">
        {description}
      </p>

      <div
        className={`mt-auto flex min-h-[76px] w-full items-center gap-3 rounded-[16px] px-4 py-3 text-left ${colors.strip}`}
      >
        <CheckCircle2 aria-hidden="true" className={`h-6 w-6 shrink-0 ${colors.check}`} />
        <span className="text-xs font-bold leading-relaxed sm:text-sm xl:text-xs">{summary}</span>
      </div>
    </article>
  );
}

function BenefitsEdgeLines() {
  return (
    <svg
      viewBox="0 0 1440 900"
      preserveAspectRatio="none"
      className="pointer-events-none absolute inset-0 h-full w-full"
      aria-hidden="true"
      fill="none"
    >
      <path d="M-40 790C105 720 205 820 355 900" stroke="#FFD966" strokeOpacity=".42" strokeWidth="2" />
      <path d="M-45 820C100 750 210 845 330 915" stroke="#FFD966" strokeOpacity=".28" strokeWidth="2" />
      <path d="M1060 900C1170 795 1260 815 1455 690" stroke="#2FAE93" strokeOpacity=".4" strokeWidth="2" />
      <path d="M-15 160C70 135 55 80 145 110" stroke="#FFD966" strokeOpacity=".52" strokeWidth="2" />
    </svg>
  );
}

function ComparePriority({ icon: Icon, label, accent, className = "" }) {
  const accentClasses = {
    yellow: "bg-yellow",
    purple: "bg-purple/15 text-purple",
    mint: "bg-mint/45",
    coral: "bg-coral/35 text-coral",
  };

  return (
    <div
      className={`z-20 flex min-h-14 w-full items-center gap-3 rounded-[18px] border border-ink/25 bg-white px-3 py-2.5 shadow-soft xl:w-[150px] ${className}`}
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${accentClasses[accent]}`}
      >
        <Icon aria-hidden="true" className="h-5 w-5" />
      </span>
      <span className="text-sm font-extrabold">{label}</span>
    </div>
  );
}

function CompareConnectorLines() {
  return (
    <svg
      viewBox="0 0 1240 560"
      preserveAspectRatio="none"
      className="pointer-events-none absolute inset-0 z-0 h-full w-full overflow-visible"
      aria-hidden="true"
      fill="none"
    >
      <path d="M70 130 Q 90 70, 120 140 T 160 120 T 190 160" stroke="#211A4D" strokeOpacity=".3" strokeWidth="2" strokeDasharray="5 7" fill="none" />
      <path d="M70 430 Q 100 480, 120 410 T 160 450 T 190 410" stroke="#211A4D" strokeOpacity=".3" strokeWidth="2" strokeDasharray="5 7" fill="none" />
      <path d="M1170 130 Q 1150 70, 1120 140 T 1080 120 T 1050 160" stroke="#211A4D" strokeOpacity=".3" strokeWidth="2" strokeDasharray="5 7" fill="none" />
      <path d="M1170 430 Q 1140 480, 1120 410 T 1080 450 T 1050 410" stroke="#211A4D" strokeOpacity=".3" strokeWidth="2" strokeDasharray="5 7" fill="none" />
    </svg>
  );
}

function LandingComparisonBoard({ schools: previewSchools }) {
  const headers = [
    { icon: School, className: "bg-mint/35" },
    { icon: GraduationCap, className: "bg-purple/10 text-purple" },
    { icon: BookOpen, className: "bg-yellow/45" },
  ];
  const rows = [
    {
      label: "SPP",
      icon: WalletCards,
      value: (school) => formatRupiahShort(school.monthlyTuition),
    },
    {
      label: "Jarak",
      icon: MapPin,
      value: (school) => `${String(school.distanceKm).replace(".", ",")} km`,
    },
    { label: "Kurikulum", icon: BookOpen, value: (school) => school.curriculum },
    {
      label: "Coding",
      icon: Code2,
      value: (school) => school.interests.includes("Coding"),
      boolean: true,
    },
    {
      label: "Tahfidz",
      icon: BookMarked,
      value: (school) =>
        school.interests.includes("Tahfidz") || school.religiousPrograms.includes("Tahfidz"),
      boolean: true,
    },
  ];

  return (
    <div className="relative z-10 mx-auto max-w-[950px] rounded-[30px] border-2 border-ink bg-warm p-3 shadow-[0_24px_55px_-28px_rgba(33,26,77,0.55)] sm:p-5 lg:p-6">
      <p className="mb-3 flex items-center justify-center gap-2 text-xs font-semibold text-ink/60 lg:hidden">
        <MoveHorizontal aria-hidden="true" className="h-4 w-4" />
        Geser untuk melihat sekolah lain
      </p>

      <div className="no-scrollbar overflow-x-auto rounded-[22px] border border-ink/15 bg-white">
        <table className="w-full min-w-[700px] table-fixed border-collapse sm:min-w-[740px] lg:min-w-full">
          <colgroup>
            <col className="w-[105px] sm:w-[120px] lg:w-[145px]" />
            {previewSchools.map((school) => (
              <col key={school.id} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="sticky left-0 z-20 border-b border-r border-ink/10 bg-white p-3" aria-label="Atribut perbandingan" />
              {previewSchools.map((school, index) => {
                const HeaderIcon = headers[index].icon;
                return (
                  <th key={school.id} scope="col" className="border-b border-ink/10 p-3 text-left align-top sm:p-4">
                    <div className={`rounded-[18px] p-3 ${headers[index].className}`}>
                      <div className="flex items-start gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-ink/15 bg-white/70">
                          <HeaderIcon aria-hidden="true" className="h-5 w-5" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-extrabold leading-snug">{school.name}</span>
                          <span className="mt-2 inline-flex items-baseline gap-1 rounded-pill border border-ink bg-yellow px-2 py-0.5">
                            <strong className="text-sm">{school.matchScore}%</strong>
                            <span className="text-[9px] font-bold uppercase text-ink/60">Match</span>
                          </span>
                        </span>
                      </div>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ label, icon: RowIcon, value, boolean }) => (
              <tr key={label} className="border-b border-ink/10 last:border-b-0">
                <th
                  scope="row"
                  className="sticky left-0 z-20 border-r border-ink/10 bg-white px-3 py-4 text-left text-xs font-bold sm:text-sm"
                >
                  <span className="flex items-center gap-2">
                    <RowIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-purple" />
                    {label}
                  </span>
                </th>
                {previewSchools.map((school) => {
                  const cellValue = value(school);
                  return (
                    <td key={school.id} className="px-4 py-4 text-center text-xs font-bold sm:text-sm">
                      {boolean ? (
                        <span className={`inline-flex items-center gap-1.5 ${cellValue ? "text-ink" : "text-ink/50"}`}>
                          {cellValue && <Check aria-hidden="true" className="h-4 w-4 text-purple" />}
                          {cellValue ? "Ya" : "Tidak"}
                        </span>
                      ) : (
                        cellValue
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 grid gap-2 text-[10px] font-bold text-ink/65 sm:grid-cols-3 sm:text-xs">
        <CompareBoardFact icon={Scale} text="Bandingkan hingga 5 sekolah" />
        <CompareBoardFact icon={Rows3} text="Informasi penting berdampingan" />
        <CompareBoardFact icon={Heart} text="Pilih sesuai kebutuhan keluarga" />
      </div>
    </div>
  );
}

function CompareBoardFact({ icon: Icon, text }) {
  return (
    <div className="flex items-center justify-center gap-2 rounded-xl bg-white/65 px-3 py-2.5 text-center">
      <Icon aria-hidden="true" className="h-4 w-4 shrink-0 text-purple" />
      <span>{text}</span>
    </div>
  );
}
